import {
  FunctionComponent,
  ReactNode,
  useState,
  PropsWithChildren,
  useEffect,
} from "react";
import styles from "./AccordionDefault.module.css";
import { LabelPairedChevronDownMdRegularIcon } from "@deriv/quill-icons";
import { useFocusHandling } from "../../hooks/useFocusHandling";

export type AccordionType = {
  className?: string;
  title?: string;
  subtitle?: string;
  customIcon?: ReactNode;
  showFocusRing?: boolean;

  /** Variant props */
  expand?: "off" | "on";
  size?: "xs" | "sm" | "md" | "lg";
  state?: "default" | "disabled";
  type?: "outline" | "fill" | "elevate" | "flush";
};

export const Accordion: FunctionComponent<PropsWithChildren<AccordionType>> = ({
  className = "",
  expand = "off",
  size = "xs",
  state = "default",
  type = "outline",
  title = "Title",
  subtitle,
  customIcon,
  showFocusRing = false,
  children,
}) => {
  const [isExpanded, setIsExpanded] = useState(expand === "on");
  const [focus, setFocus] = useState(showFocusRing);
  const accordionId = `quill-accordion-${size}-${type}-${state}`;
  const isClickable = state !== "disabled";

  const handleToggle = () => {
    if (isClickable) {
      setIsExpanded(!isExpanded);
    }
  };

  const { handleFocus, handleBlur } = useFocusHandling({
    isDisabled: !isClickable,
    setHighlight: setFocus,
    onEnter: () => handleToggle(),
  });

  useEffect(() => {
    setIsExpanded(expand === "on");
  }, [expand]);

  return (
    <div
      className={[styles.root, className].join(" ")}
      data-expand={isExpanded ? "on" : "off"}
      data-size={size}
      data-state={state}
      data-type={type}
      data-id={accordionId}
    >
      <div
        className={[
          styles.header,
          isClickable ? "quill-ui-next-pointer" : "",
          "quill-ui-next-component",
        ].join(" ")}
        onClick={handleToggle}
        role={isClickable ? "button" : undefined}
        tabIndex={isClickable ? 0 : undefined}
        onFocus={handleFocus}
        onBlur={handleBlur}
      >
        {focus && <div className={styles.bgFocus} />}
        {customIcon && (
          <div className={styles.placeholderIcon}>
            <div className={`${styles.icon1} accordion-icon`}>{customIcon}</div>
          </div>
        )}
        <div className={styles.titleSubtitle}>
          <b className={styles.title}>{title}</b>
          {subtitle && <div className={styles.subtitle}>{subtitle}</div>}
        </div>
        <div className={styles.iconChevron}>
          <div className={`${styles.icon2} accordion-icon`}>
            <LabelPairedChevronDownMdRegularIcon
              className={`quill-ui-next-rotatable ${
                isExpanded && "quill-ui-next-rotate"
              }`}
            />
          </div>
        </div>
      </div>
      {children && (
        <div className={styles.body}>
          <div
            className={`${styles.bodyContent} quill-ui-next-accordion-content`}
          >
            {children}
          </div>
        </div>
      )}
    </div>
  );
};

export default Accordion;
